import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import slugify from "slugify";

import {
  Agent,
  AgentVersion,
  AgentVersionConfigurationInput,
  AgentVersionStatus,
} from "src/agents/types";
import { agentManagementApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import type { AgentVersionLLMConfig } from "src/clients/agent-management";

const PREFIX_CACHE_KEY = "agent-management";

export const useAgentManagementClient = () => {
  const { data: workspace } = useCurrentWorkspace();

  return useMemo(
    () =>
      workspace?.base_url ? agentManagementApi(workspace.base_url) : undefined,
    [workspace?.base_url],
  );
};

type CreateAgentParams = {
  name: string;
  description: string;
};

type UpdateAgentParams = {
  id: string;
  name: string;
  description: string;
};

type CreateAgentVersionParams = {
  agentId: string;
  versionName: string;
  description: string;
  sourceVersionId?: string;
};

type PublishAgentVersionParams = {
  agentId: string;
  versionId: string;
  etag: string;
};

type DeleteAgentVersionParams = {
  agentId: string;
  versionId: string;
};

type UpdateAgentVersionParams = {
  agentId: string;
  versionId: string;
  etag: string;
  name: string;
  description: string;
  configuration: AgentVersionConfigurationInput;
};

export const useAgents = () => {
  const client = useAgentManagementClient();

  return useQuery({
    queryKey: [PREFIX_CACHE_KEY, "agents"],
    queryFn: async () => {
      if (!client) {
        throw new Error("Agent management client not available");
      }
      const response = await client.listAgentsAgentsManagementApiV0AgentsGet(
        {},
      );
      return response.data.agents;
    },
    enabled: !!client,
  });
};

export const useAgent = (agentId: string) => {
  const client = useAgentManagementClient();

  return useQuery({
    queryKey: [PREFIX_CACHE_KEY, "agents", agentId],
    queryFn: async () => {
      if (!client) {
        throw new Error("Agent management client not available");
      }
      const response =
        await client.getAgentAgentsManagementApiV0AgentsAgentIdGet({ agentId });
      return response.data.agent;
    },
    enabled: !!client && !!agentId,
  });
};

export const useAgentVersions = (
  agentId: string,
  status?: AgentVersionStatus,
) => {
  const client = useAgentManagementClient();

  return useQuery({
    queryKey: [PREFIX_CACHE_KEY, "agents", agentId, "versions", status],
    queryFn: async () => {
      if (!client) {
        throw new Error("Agent management client not available");
      }
      const response =
        await client.listVersionsAgentsManagementApiV0AgentsAgentIdVersionsGet({
          agentId,
          status,
        });
      return response.data.versions;
    },
    enabled: !!client && !!agentId,
  });
};

export const useAgentVersion = (agentId: string, versionId: string) => {
  const client = useAgentManagementClient();

  return useQuery({
    queryKey: [PREFIX_CACHE_KEY, "agents", agentId, "versions", versionId],
    queryFn: async () => {
      if (!client) {
        throw new Error("Agent management client not available");
      }
      const response =
        await client.getVersionAgentsManagementApiV0AgentsAgentIdVersionsVersionIdGet(
          { agentId, versionId },
        );
      return response.data.agent_version;
    },
    enabled: !!client && !!agentId && !!versionId,
  });
};

export const useCreateAgent = () => {
  const client = useAgentManagementClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateAgentParams) => {
      if (!client) {
        throw new Error("Agent management client not available");
      }
      const response = await client.createAgentAgentsManagementApiV0AgentsPost({
        agentCreate: {
          slug: slugify(params.name, { lower: true, strict: true }),
          name: params.name,
          description: params.description,
        },
      });
      return response.data.agent;
    },
    onSuccess: (newAgent) => {
      // Update agents list
      queryClient.setQueryData<Agent[]>(
        [PREFIX_CACHE_KEY, "agents"],
        (oldAgents) => (oldAgents ? [...oldAgents, newAgent] : [newAgent]),
      );
      // Set individual agent query
      queryClient.setQueryData(
        [PREFIX_CACHE_KEY, "agents", newAgent.id],
        newAgent,
      );
    },
  });
};

export const useUpdateAgent = () => {
  const client = useAgentManagementClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateAgentParams) => {
      if (!client) {
        throw new Error("Agent management client not available");
      }
      const response =
        await client.updateAgentAgentsManagementApiV0AgentsAgentIdPut({
          agentId: params.id,
          agentUpdate: {
            name: params.name,
            description: params.description,
          },
        });
      return response.data.agent;
    },
    onSuccess: (updatedAgent) => {
      // Update agents list
      queryClient.setQueryData<Agent[]>(
        [PREFIX_CACHE_KEY, "agents"],
        (oldAgents) =>
          oldAgents?.map((agent) =>
            agent.id === updatedAgent.id ? updatedAgent : agent,
          ),
      );
      // Update individual agent query
      queryClient.setQueryData(
        [PREFIX_CACHE_KEY, "agents", updatedAgent.id],
        updatedAgent,
      );
    },
  });
};

export const useDeleteAgent = () => {
  const client = useAgentManagementClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (agentId: string) => {
      if (!client) {
        throw new Error("Agent management client not available");
      }
      await client.deleteAgentAgentsManagementApiV0AgentsAgentIdDelete({
        agentId,
      });
      return agentId;
    },
    onSuccess: (deletedAgentId) => {
      // Remove from agents list
      queryClient.setQueryData<Agent[]>(
        [PREFIX_CACHE_KEY, "agents"],
        (oldAgents) =>
          oldAgents?.filter((agent) => agent.id !== deletedAgentId),
      );
      // Remove individual agent query
      queryClient.removeQueries({
        queryKey: [PREFIX_CACHE_KEY, "agents", deletedAgentId],
      });
    },
  });
};

export const DEFAULT_MODEL_CONFIG: AgentVersionLLMConfig = {
  model: "gpt-5",
  system_prompt: "You are a helpful AI assistant.",
  temperature: 0.2,
};

export const useCreateAgentVersion = () => {
  const client = useAgentManagementClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateAgentVersionParams) => {
      if (!client) {
        throw new Error("Agent management client not available");
      }
      const response =
        await client.createVersionAgentsManagementApiV0AgentsAgentIdVersionsPost(
          {
            agentId: params.agentId,
            agentVersionCreate: {
              name: params.versionName,
              description: params.description,
              ...(params.sourceVersionId
                ? { source_version_id: params.sourceVersionId }
                : {
                    configuration: {
                      model_config: DEFAULT_MODEL_CONFIG,
                      tools: [],
                      execution_config: {
                        max_steps_per_session: 50,
                      },
                    },
                  }),
            },
          },
        );
      return response.data.agent_version;
    },
    onSuccess: (newVersion, params) => {
      // Add new version to the versions list
      queryClient.setQueryData<AgentVersion[]>(
        [PREFIX_CACHE_KEY, "agents", params.agentId, "versions", undefined],
        (oldVersions) =>
          oldVersions ? [...oldVersions, newVersion] : [newVersion],
      );
      // Set individual version query
      queryClient.setQueryData(
        [PREFIX_CACHE_KEY, "agents", params.agentId, "versions", newVersion.id],
        newVersion,
      );
      // Increment draft count since new versions are always drafts
      queryClient.setQueryData<Agent[]>(
        [PREFIX_CACHE_KEY, "agents"],
        (oldAgents) =>
          oldAgents?.map((agent) =>
            agent.id === params.agentId
              ? {
                  ...agent,
                  draft_versions_count: (agent.draft_versions_count ?? 0) + 1,
                }
              : agent,
          ),
      );
      queryClient.setQueryData<Agent>(
        [PREFIX_CACHE_KEY, "agents", params.agentId],
        (agent) =>
          agent
            ? {
                ...agent,
                draft_versions_count: (agent.draft_versions_count ?? 0) + 1,
              }
            : undefined,
      );
    },
  });
};

export const usePublishAgentVersion = () => {
  const client = useAgentManagementClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: PublishAgentVersionParams) => {
      if (!client) {
        throw new Error("Agent management client not available");
      }
      const response =
        await client.publishVersionAgentsManagementApiV0AgentsAgentIdVersionsVersionIdPublishPost(
          {
            agentId: params.agentId,
            versionId: params.versionId,
            ifMatch: params.etag,
          },
        );
      return response.data.agent_version;
    },
    onSuccess: (publishedVersion, params) => {
      // Update versions list
      queryClient.setQueryData<AgentVersion[]>(
        [PREFIX_CACHE_KEY, "agents", params.agentId, "versions", undefined],
        (oldVersions) =>
          oldVersions?.map((version) =>
            version.id === params.versionId ? publishedVersion : version,
          ),
      );
      // Update individual version query
      queryClient.setQueryData(
        [
          PREFIX_CACHE_KEY,
          "agents",
          params.agentId,
          "versions",
          params.versionId,
        ],
        publishedVersion,
      );
      // Publishing converts a draft to published
      queryClient.setQueryData<Agent[]>(
        [PREFIX_CACHE_KEY, "agents"],
        (oldAgents) =>
          oldAgents?.map((agent) =>
            agent.id === params.agentId
              ? {
                  ...agent,
                  draft_versions_count: (agent.draft_versions_count ?? 0) - 1,
                  published_versions_count:
                    (agent.published_versions_count ?? 0) + 1,
                }
              : agent,
          ),
      );
      queryClient.setQueryData<Agent>(
        [PREFIX_CACHE_KEY, "agents", params.agentId],
        (agent) =>
          agent
            ? {
                ...agent,
                draft_versions_count: (agent.draft_versions_count ?? 0) - 1,
                published_versions_count:
                  (agent.published_versions_count ?? 0) + 1,
              }
            : undefined,
      );
    },
  });
};

export const useDeleteAgentVersion = () => {
  const client = useAgentManagementClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteAgentVersionParams) => {
      if (!client) {
        throw new Error("Agent management client not available");
      }
      await client.deleteVersionAgentsManagementApiV0AgentsAgentIdVersionsVersionIdDelete(
        {
          agentId: params.agentId,
          versionId: params.versionId,
        },
      );
      return params;
    },
    onSuccess: (deletedParams) => {
      // Remove from versions list
      queryClient.setQueryData<AgentVersion[]>(
        [
          PREFIX_CACHE_KEY,
          "agents",
          deletedParams.agentId,
          "versions",
          undefined,
        ],
        (oldVersions) =>
          oldVersions?.filter(
            (version) => version.id !== deletedParams.versionId,
          ),
      );
      // Remove individual version query
      queryClient.removeQueries({
        queryKey: [
          PREFIX_CACHE_KEY,
          "agents",
          deletedParams.agentId,
          "versions",
          deletedParams.versionId,
        ],
      });
      // Decrement draft count since deleted versions are always drafts
      queryClient.setQueryData<Agent[]>(
        [PREFIX_CACHE_KEY, "agents"],
        (oldAgents) =>
          oldAgents?.map((agent) =>
            agent.id === deletedParams.agentId
              ? {
                  ...agent,
                  draft_versions_count: (agent.draft_versions_count ?? 0) - 1,
                }
              : agent,
          ),
      );
      queryClient.setQueryData<Agent>(
        [PREFIX_CACHE_KEY, "agents", deletedParams.agentId],
        (agent) =>
          agent
            ? {
                ...agent,
                draft_versions_count: (agent.draft_versions_count ?? 0) - 1,
              }
            : undefined,
      );
    },
  });
};

export const AGENT_VERSION_CONFIGURATION_MUTATION_KEY = [
  PREFIX_CACHE_KEY,
  "update-version-configuration",
];

export const useUpdateAgentVersion = () => {
  const client = useAgentManagementClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_VERSION_CONFIGURATION_MUTATION_KEY,
    mutationFn: async (params: UpdateAgentVersionParams) => {
      if (!client) {
        throw new Error("Agent management client not available");
      }
      const response =
        await client.updateVersionAgentsManagementApiV0AgentsAgentIdVersionsVersionIdPut(
          {
            agentId: params.agentId,
            versionId: params.versionId,
            ifMatch: params.etag,
            agentVersionUpdate: {
              name: params.name,
              description: params.description,
              configuration: params.configuration,
            },
          },
        );
      return response.data.agent_version;
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({
        queryKey: [
          PREFIX_CACHE_KEY,
          "agents",
          params.agentId,
          "versions",
          params.versionId,
        ],
      });

      const previousVersion = queryClient.getQueryData<AgentVersion>([
        PREFIX_CACHE_KEY,
        "agents",
        params.agentId,
        "versions",
        params.versionId,
      ]);

      const previousVersions = queryClient.getQueryData<AgentVersion[]>([
        PREFIX_CACHE_KEY,
        "agents",
        params.agentId,
        "versions",
        undefined,
      ]);

      // Optimistically update individual version
      queryClient.setQueryData<AgentVersion>(
        [
          PREFIX_CACHE_KEY,
          "agents",
          params.agentId,
          "versions",
          params.versionId,
        ],
        (oldVersion) =>
          oldVersion
            ? {
                ...oldVersion,
                name: params.name,
                description: params.description,
                configuration: params.configuration,
              }
            : undefined,
      );

      // Optimistically update versions list
      queryClient.setQueryData<AgentVersion[]>(
        [PREFIX_CACHE_KEY, "agents", params.agentId, "versions", undefined],
        (oldVersions) =>
          oldVersions?.map((version) =>
            version.id === params.versionId
              ? {
                  ...version,
                  name: params.name,
                  description: params.description,
                  configuration: params.configuration,
                }
              : version,
          ),
      );

      return { previousVersion, previousVersions };
    },
    onError: (_error, params, context) => {
      if (context?.previousVersion) {
        queryClient.setQueryData(
          [
            PREFIX_CACHE_KEY,
            "agents",
            params.agentId,
            "versions",
            params.versionId,
          ],
          context.previousVersion,
        );
      }
      if (context?.previousVersions) {
        queryClient.setQueryData(
          [PREFIX_CACHE_KEY, "agents", params.agentId, "versions", undefined],
          context.previousVersions,
        );
      }
    },
    onSuccess: (updatedVersion, params) => {
      // Update individual version with server response
      queryClient.setQueryData(
        [
          PREFIX_CACHE_KEY,
          "agents",
          params.agentId,
          "versions",
          params.versionId,
        ],
        updatedVersion,
      );
      // Update versions list with server response
      queryClient.setQueryData<AgentVersion[]>(
        [PREFIX_CACHE_KEY, "agents", params.agentId, "versions", undefined],
        (oldVersions) =>
          oldVersions?.map((version) =>
            version.id === params.versionId ? updatedVersion : version,
          ),
      );
    },
  });
};
